/** @format */

import {Box, LinearProgress, Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import * as R from 'ramda';
import {Trans} from 'react-i18next';

import {Link as RouterLink, useNavigate} from 'react-router-dom';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {makeStyles} from '@mui/styles';

import Breadcrumbs from 'components/Breadcrumbs';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import ConnectedProvidersNew from 'components/icons/ConnectedProvidersNew';
import ButtonPlate from 'components/plates/ButtonPlate';
import RestrictionBlock from 'components/RestrictionBlock';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {useGetCurrentUserQuery} from './graphql/__generated__';
import {CompanyType} from './types';

const useStyles = makeStyles(theme => ({
  headerTitle: {
    fontSize: '24px',
    fontWeight: 700,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: '16px',
    },
  },
  row: {
    padding: '16px',
    borderRadius: '8px',
    background: '#202020',
  },
  companyTitle: {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '26px',
    letterSpacing: '0.169px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      display: 'flex',
    },
  },
  companyHeader: {
    width: '100%',
    marginRight: '10px',
  },
  companyArrowPic: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: '#2B2B2B',
  },
}));

const Connect = () => {
  const navigate = useNavigate();
  const {mixpanel} = useMixpanelContext();

  const classes = useStyles();

  const {loading, error, data} = useGetCurrentUserQuery();

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const breadcrumbs = [
    {
      href: '/home/settings',
      title: <Trans>Настройки</Trans>,
    },
    {
      href: '',
      title: <Trans>Проекты</Trans>,
    },
  ];

  const isRestricted = data.currentUser.restrictions.projectCreationRestricted === true;

  const {companies} = data.currentUser;
  const sortedCompanies = R.pipe(
    R.filter<CompanyType>(v => !v.isClientAccess),
    R.sortBy(R.prop('name')),
  )(companies);

  const renderCompanyNew = (company: CompanyType) => {
    const handleClick = () => {
      navigate({
        pathname: '/home/project/edit',
        search: `?companyId=${company.id}`,
      });
    };

    return (
      <ButtonPlate onClick={handleClick}>
        <Box alignItems='center' display='flex' flexGrow={1} justifyContent='space-between'>
          <Box
            display='flex'
            flexWrap={'wrap'}
            alignItems={'center'}
            justifyContent={'space-between'}
            className={classes.companyHeader}
          >
            <Typography className={classes.companyTitle}>{company.name}</Typography>

            {company.tokens.length > 0 && (
              <ConnectedProvidersNew
                adTypes={['CONTEXT', 'TARGET', 'STATS']}
                tokens={company.tokens}
              />
            )}
          </Box>

          <Box className={classes.companyArrowPic}>
            <ChevronRightIcon fontSize='small' />
          </Box>
        </Box>
      </ButtonPlate>
    );
  };

  const handleClickAddNew = () => {
    mixpanel.track('click_settings_add_company_button');
  };

  return (
    <>
      <Box>
        <Box mb={10}>
          <Breadcrumbs items={breadcrumbs} />
        </Box>

        <Box display='flex' flexWrap='wrap' alignItems='center' justifyContent='space-between'>
          <Typography className={classes.headerTitle}>
            <Trans>Управление проектами</Trans>
          </Typography>

          <Box>
            <PrimaryMediumButton
              variant='contained'
              onClick={handleClickAddNew}
              disabled={isRestricted}
              data-testid='add-project'
              sx={theme => ({
                '&:disabled': {
                  backgroundColor: theme.palette.figma_gray_dark_2?.main,
                  color: theme.palette.figma_gray_middle_dark?.main,
                },
              })}
              {...(!isRestricted && {
                component: RouterLink,
                to: {pathname: '/home/project/new'},
              })}
            >
              <Box mr={2}>
                <AddCircleOutlineOutlinedIcon fontSize='small' />
              </Box>

              <Trans>Добавить проект</Trans>
            </PrimaryMediumButton>
          </Box>
        </Box>

        {isRestricted && <RestrictionBlock mt={6} />}

        <Box mt={6} mb={6} className={classes.row}>
          <Trans>
            В этом разделе вы можете добавлять или удалять проекты, подключать или отключать
            рекламные кабинеты.
          </Trans>
        </Box>

        {sortedCompanies.map((v, i) => (
          <Box key={i} mt={2}>
            {renderCompanyNew(v)}
          </Box>
        ))}
      </Box>
    </>
  );
};

export default Connect;
